import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import AddIcon from "../../components/Atomos/Icons/AddIcon";
import {
  getEmailRecipientFilterFields,
  getEmailRecipientFilterValueOptions,
  checkEmailRecipientMailAddressesExist,
} from "../../Services/EmailRecipientFilterOptions";
import ConditionRow from "./ConditionRow";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import { firmShortLabel } from "../../utils/firmDisplayName";
import {
  RecipientCondition,
  Operator,
  ConditionOperator,
  ConditionFieldConfig,
  ConditionValueOption,
} from "./types";

interface ConditionBuilderProps {
  operator: Operator;
  onEmpty?: () => void;
  excludedEmails?: string[];
  onRemoveExclusion?: (email: string) => void;
  initialConditions?: RecipientCondition[];
  resetKey?: string;
  onConditionsChange?: (conditions: RecipientCondition[]) => void;
  canRemoveLastCondition?: boolean;
}

const FIRM_COMPANY_FIELD_ID = "firm-company";

function withStaffFirmLabels(
  fieldId: string,
  options: ConditionValueOption[],
  firmShortNameById: Map<string, string>,
): ConditionValueOption[] {
  if (fieldId !== FIRM_COMPANY_FIELD_ID || firmShortNameById.size === 0) {
    return options;
  }
  return options.map((option) => {
    const shortName = firmShortNameById.get(option.id);
    if (!shortName) {
      return option;
    }
    return { ...option, name: shortName };
  });
}

const getDefaultOperator = (
  fieldConfig?: ConditionFieldConfig,
): ConditionOperator => {
  if (!fieldConfig || fieldConfig.operators.length === 0) {
    return "is-one-of";
  }
  if (fieldConfig.operators.includes("is-one-of")) {
    return "is-one-of";
  }
  if (fieldConfig.operators.includes("is")) {
    return "is";
  }
  return fieldConfig.operators[0];
};

const MAIL_ADDRESS_FIELD_ID = "mail-address";

const parseMailAddressEmails = (value: string): string[] =>
  value
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean)
    .filter(
      (email, index, collection) =>
        index ===
        collection.findIndex(
          (candidate) => candidate.toLowerCase() === email.toLowerCase(),
        ),
    );

const buildMissingMailAddressMessage = (
  missingEmails: string[],
): string | null => {
  if (!missingEmails.length) {
    return null;
  }
  if (missingEmails.length === 1) {
    return `Email not found in Person or Guest Speaker: ${missingEmails[0]}`;
  }
  return `Emails not found in Person or Guest Speaker: ${missingEmails.join(", ")}`;
};

export default function ConditionBuilder({
  operator,
  onEmpty,
  excludedEmails = [],
  onRemoveExclusion,
  initialConditions,
  resetKey,
  onConditionsChange,
  canRemoveLastCondition = true,
}: ConditionBuilderProps): React.ReactElement {
  const { firmData } = UseFirmList();
  const firmShortNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const firm of firmData) {
      if (!firm?.id) {
        continue;
      }
      const label = firmShortLabel(firm);
      if (label) {
        names.set(firm.id, label);
      }
    }
    return names;
  }, [firmData]);
  const idCounter = useRef(1);
  const onConditionsChangeRef = useRef(onConditionsChange);

  const [fieldOptions, setFieldOptions] = useState<ConditionFieldConfig[]>([]);
  const [isFieldOptionsLoading, setIsFieldOptionsLoading] = useState(false);
  const [valueOptionsByField, setValueOptionsByField] = useState<
    Record<string, ConditionValueOption[]>
  >({});
  const [isValueOptionsLoadingByField, setIsValueOptionsLoadingByField] =
    useState<Record<string, boolean>>({});
  const [mailAddressValidationByConditionId, setMailAddressValidationByConditionId] =
    useState<Record<string, string>>({});
  const [
    isMailAddressValidationLoadingByConditionId,
    setIsMailAddressValidationLoadingByConditionId,
  ] = useState<Record<string, boolean>>({});

  const fieldMap = useMemo<Record<string, ConditionFieldConfig>>(
    () =>
      fieldOptions.reduce((acc, option) => {
        acc[option.id] = option;
        return acc;
      }, {} as Record<string, ConditionFieldConfig>),
    [fieldOptions],
  );

  const initialConditionsKey = useMemo(
    () => JSON.stringify(initialConditions ?? []),
    [initialConditions],
  );

  const validateMailAddressConditionsBatch = useCallback(
    async (conditionsToValidate: RecipientCondition[]) => {
      const mailAddressConditions = conditionsToValidate.filter(
        (condition) =>
          condition.fieldId === MAIL_ADDRESS_FIELD_ID &&
          parseMailAddressEmails(condition.valueId).length > 0,
      );

      if (!mailAddressConditions.length) {
        return;
      }

      setIsMailAddressValidationLoadingByConditionId((prev) => {
        const next = { ...prev };
        mailAddressConditions.forEach((condition) => {
          next[condition.id] = true;
        });
        return next;
      });

      const allEmails = Array.from(
        new Set(
          mailAddressConditions.flatMap((condition) =>
            parseMailAddressEmails(condition.valueId),
          ),
        ),
      );

      const validationResults = await checkEmailRecipientMailAddressesExist(
        allEmails,
      );
      const existsByEmail = new Map(
        validationResults.map((result) => [
          result.email.toLowerCase(),
          result.exists,
        ]),
      );

      const validationMessages: Record<string, string> = {};
      mailAddressConditions.forEach((condition) => {
        const missingEmails = parseMailAddressEmails(condition.valueId).filter(
          (email) => !existsByEmail.get(email.toLowerCase()),
        );
        const message = buildMissingMailAddressMessage(missingEmails);
        if (message) {
          validationMessages[condition.id] = message;
        }
      });

      setMailAddressValidationByConditionId((prev) => {
        const next = { ...prev };
        mailAddressConditions.forEach((condition) => {
          if (validationMessages[condition.id]) {
            next[condition.id] = validationMessages[condition.id];
          } else {
            delete next[condition.id];
          }
        });
        return next;
      });

      setIsMailAddressValidationLoadingByConditionId((prev) => {
        const next = { ...prev };
        mailAddressConditions.forEach((condition) => {
          delete next[condition.id];
        });
        return next;
      });
    },
    [],
  );

  const createCondition = useCallback(
    (): RecipientCondition => ({
      id: String(idCounter.current++),
      fieldId: "",
      conditionOperator: "is-one-of",
      valueId: "",
    }),
    [],
  );

  const [conditions, setConditions] = useState<RecipientCondition[]>(() => {
    if (initialConditions && initialConditions.length > 0) {
      return initialConditions;
    }
    return [createCondition()];
  });

  const loadFieldOptions = useCallback(async () => {
    setIsFieldOptionsLoading(true);
    try {
      const fields = await getEmailRecipientFilterFields();
      setFieldOptions(fields);
    } finally {
      setIsFieldOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFieldOptions();
  }, [loadFieldOptions]);

  const loadValueOptionsForField = useCallback(
    async (fieldId: string) => {
      if (!fieldId) {
        return;
      }
      if (valueOptionsByField[fieldId]) {
        return;
      }
      if (isValueOptionsLoadingByField[fieldId]) {
        return;
      }

      const fieldConfig = fieldMap[fieldId];
      if (!fieldConfig || fieldConfig.inputType !== "select") {
        return;
      }

      setIsValueOptionsLoadingByField((prev) => ({ ...prev, [fieldId]: true }));
      try {
        const options = await getEmailRecipientFilterValueOptions(fieldId);
        setValueOptionsByField((prev) => ({ ...prev, [fieldId]: options }));
      } finally {
        setIsValueOptionsLoadingByField((prev) => ({ ...prev, [fieldId]: false }));
      }
    },
    [fieldMap, valueOptionsByField, isValueOptionsLoadingByField],
  );

  useEffect(() => {
    if (!conditions.length) {
      return;
    }
    const uniqueFieldIds = Array.from(
      new Set(conditions.map((condition) => condition.fieldId).filter(Boolean)),
    );
    uniqueFieldIds.forEach((fieldId) => {
      void loadValueOptionsForField(fieldId);
    });
  }, [conditions, loadValueOptionsForField]);

  useEffect(() => {
    if (!fieldOptions.length) {
      return;
    }
    setConditions((prev) => {
      let hasChanges = false;
      const next = prev.map((condition) => {
        const fieldConfig = fieldMap[condition.fieldId];
        if (!fieldConfig) {
          return condition;
        }
        if (fieldConfig.operators.includes(condition.conditionOperator)) {
          return condition;
        }
        hasChanges = true;
        return {
          ...condition,
          conditionOperator: getDefaultOperator(fieldConfig),
        };
      });
      return hasChanges ? next : prev;
    });
  }, [fieldMap, fieldOptions.length]);

  useEffect(() => {
    if (initialConditions && initialConditions.length > 0) {
      const nextConditions = initialConditions.map((condition) => ({
        ...condition,
      }));
      setConditions(nextConditions);
      setMailAddressValidationByConditionId({});
      setIsMailAddressValidationLoadingByConditionId({});
      const maxNumericId = initialConditions.reduce((max, item) => {
        const parsed = Number(item.id);
        return Number.isFinite(parsed) ? Math.max(max, parsed) : max;
      }, 0);
      idCounter.current = Math.max(maxNumericId + 1, 1);
      void validateMailAddressConditionsBatch(nextConditions);
      return;
    }

    idCounter.current = 1;
    setConditions([createCondition()]);
    setMailAddressValidationByConditionId({});
    setIsMailAddressValidationLoadingByConditionId({});
  }, [
    resetKey,
    initialConditionsKey,
    createCondition,
    initialConditions,
    validateMailAddressConditionsBatch,
  ]);

  useEffect(() => {
    onConditionsChangeRef.current = onConditionsChange;
  }, [onConditionsChange]);

  useEffect(() => {
    onConditionsChangeRef.current?.(conditions);
  }, [conditions]);

  const handleAddCondition = useCallback(() => {
    setConditions((prev) => [...prev, createCondition()]);
  }, [createCondition]);

  const handleRemoveCondition = useCallback(
    (id: string) => {
      setConditions((prev) => {
        if (prev.length === 1 && !canRemoveLastCondition) {
          return prev;
        }
        const newConditions = prev.filter((c) => c.id !== id);
        if (newConditions.length === 0 && onEmpty) {
          onEmpty();
        }
        return newConditions;
      });
      setMailAddressValidationByConditionId((prev) => {
        if (!(id in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setIsMailAddressValidationLoadingByConditionId((prev) => {
        if (!(id in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    [canRemoveLastCondition, onEmpty],
  );

  const handleFieldChange = useCallback(
    (id: string, fieldId: string) => {
      setConditions((prev) =>
        prev.map((condition) => {
          if (condition.id !== id) {
            return condition;
          }
          const config = fieldMap[fieldId];
          return {
            ...condition,
            fieldId,
            conditionOperator: getDefaultOperator(config),
            valueId: "",
          };
        }),
      );
      setMailAddressValidationByConditionId((prev) => {
        if (!(id in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (fieldId) {
        void loadValueOptionsForField(fieldId);
      }
    },
    [fieldMap, loadValueOptionsForField],
  );

  const handleConditionOperatorChange = useCallback(
    (id: string, conditionOperator: ConditionOperator) => {
      setConditions((prev) =>
        prev.map((condition) => {
          if (condition.id !== id) {
            return condition;
          }
          const supportsMultiValue =
            conditionOperator === "is" ||
            conditionOperator === "is-one-of" ||
            conditionOperator === "is-not";
          const hasMultipleValues = condition.valueId.includes("|");
          const valueId =
            !supportsMultiValue && hasMultipleValues
              ? condition.valueId.split("|")[0] || ""
              : condition.valueId;

          return { ...condition, conditionOperator, valueId };
        }),
      );
    },
    [],
  );

  const handleValueChange = useCallback((id: string, valueId: string) => {
    setConditions((prev) =>
      prev.map((condition) =>
        condition.id === id ? { ...condition, valueId } : condition,
      ),
    );
    setMailAddressValidationByConditionId((prev) => {
      if (!(id in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const handleMailAddressBlur = useCallback(
    async (id: string, value: string) => {
      const emails = parseMailAddressEmails(value);

      if (!emails.length) {
        setMailAddressValidationByConditionId((prev) => {
          if (!(id in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setIsMailAddressValidationLoadingByConditionId((prev) => {
          if (!(id in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[id];
          return next;
        });
        return;
      }

      await validateMailAddressConditionsBatch([
        {
          id,
          fieldId: MAIL_ADDRESS_FIELD_ID,
          conditionOperator: "is",
          valueId: value,
        },
      ]);
    },
    [validateMailAddressConditionsBatch],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="relative flex flex-col gap-4">
        <div
          className="absolute w-0.5 bg-gray-font"
          style={{
            left: "36.5px",
            top: "1.25rem",
            bottom: "1.25rem",
          }}
        />

        {conditions.map((condition) => (
          <ConditionRow
            key={`${resetKey || "manual"}-${condition.id}`}
            condition={condition}
            operator={operator}
            fieldOptions={fieldOptions}
            fieldConfig={fieldMap[condition.fieldId]}
            valueOptions={withStaffFirmLabels(
              condition.fieldId,
              valueOptionsByField[condition.fieldId] || [],
              firmShortNameById,
            )}
            isFieldOptionsLoading={isFieldOptionsLoading}
            isValueOptionsLoading={
              !!isValueOptionsLoadingByField[condition.fieldId]
            }
            onFieldChange={handleFieldChange}
            onConditionOperatorChange={handleConditionOperatorChange}
            onValueChange={handleValueChange}
            onMailAddressBlur={handleMailAddressBlur}
            mailAddressValidationMessage={
              mailAddressValidationByConditionId[condition.id]
            }
            isMailAddressValidationLoading={
              !!isMailAddressValidationLoadingByConditionId[condition.id]
            }
            canRemove={conditions.length > 1 || canRemoveLastCondition}
            onRemove={handleRemoveCondition}
          />
        ))}

        {excludedEmails.map((email) => {
          const excludedCondition: RecipientCondition = {
            id: `excluded-${email}`,
            fieldId: "mail-address",
            conditionOperator: "is-not",
            valueId: email,
          };
          return (
            <ConditionRow
              key={`excluded-${email}`}
              condition={excludedCondition}
              operator={operator}
              fieldOptions={fieldOptions}
              fieldConfig={fieldMap["mail-address"]}
              valueOptions={[]}
              isFieldOptionsLoading={isFieldOptionsLoading}
              isValueOptionsLoading={false}
              onFieldChange={() => {}}
              onConditionOperatorChange={() => {}}
              onValueChange={() => {}}
              onMailAddressBlur={() => {}}
              mailAddressValidationMessage=""
              isMailAddressValidationLoading={false}
              onRemove={() => onRemoveExclusion?.(email)}
              readOnly
            />
          );
        })}

        <div>
          <button
            type="button"
            onClick={handleAddCondition}
            className="inline-flex items-center justify-center gap-3 min-h-[42px] px-5 bg-white font-decimal font-medium text-[18px] leading-[20px] text-gray-font whitespace-nowrap hover:bg-gray-50 transition-colors"
            style={{
              border: "1px solid #474F56",
              WebkitFontSmoothing: "antialiased",
            }}
          >
            ADD CONDITION
            <AddIcon />
          </button>
        </div>
      </div>
    </div>
  );
}
